import axios from "axios";
import { ensureCsrfToken } from "../lib/csrf";

const API_BASE = "/api/digital-risk";

export async function fetchPortfolioFinancing(
  strategy,
  simulations
) {
  const csrfToken = await ensureCsrfToken();

  const payload = {
    simulations: Number(simulations),
  };

  /*
   * When no strategy is supplied, only simulations
   * are sent. The backend can then resolve the
   * current/default financing policy.
   */
  if (strategy) {
    payload.premium = Number(
      strategy.premium ?? 0
    );

    payload.deductible = Number(
      strategy.deductible ?? 0
    );

    payload.policy_limit = Number(
      strategy.policy_limit ?? 0
    );

    payload.coinsurance = Number(
      strategy.coinsurance ?? 1
    );
  }

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/portfolio/`,
    payload,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function fetchFinancingStrategies(
  simulations
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/strategies/`,
    {
      simulations,
    },
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function fetchActiveFinancingScenarios() {
  const { data } = await axios.get(
    `${API_BASE}/fair-scenarios/`,
    {
      params: {
        status: "Active",
      },
      withCredentials: true,
    }
  );

  return Array.isArray(data)
    ? data
    : data?.results || [];
}

export async function fetchScenarioFinancing(
  scenarioId,
  strategy,
  simulations
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/scenario/${scenarioId}/`,
    {
      premium: Number(
        strategy?.premium || 0
      ),
      deductible: Number(
        strategy?.deductible || 0
      ),
      policy_limit: Number(
        strategy?.policy_limit || 0
      ),
      coinsurance: Number(
        strategy?.coinsurance ?? 1
      ),
      simulations: Number(
        simulations
      ),
    },
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function fetchFinancingCoverages() {
  const { data } = await axios.get(
    `${API_BASE}/risk-financing/coverages/`,
    {
      withCredentials: true,
    }
  );

  return {
    count: Number(data?.count ?? 0),
    results: Array.isArray(data?.results)
      ? data.results
      : [],
  };
}

export async function linkCoverageToScenario(
  coverageId,
  payload
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/coverages/${coverageId}/scenarios/`,
    payload,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function unlinkCoverageFromScenario(
  coverageId,
  scenarioId
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.delete(
    `${API_BASE}/risk-financing/coverages/${coverageId}/scenarios/${scenarioId}/`,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function createFinancingCoverage(
  payload
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/coverages/`,
    payload,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function updateFinancingCoverage(
  coverageId,
  payload
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.patch(
    `${API_BASE}/risk-financing/coverages/${coverageId}/`,
    payload,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function changeFinancingCoverageStatus(
  coverageId,
  status
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.post(
    `${API_BASE}/risk-financing/coverages/${coverageId}/status/`,
    {
      status,
    },
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}

export async function deleteFinancingCoverage(
  coverageId
) {
  const csrfToken = await ensureCsrfToken();

  const { data } = await axios.delete(
    `${API_BASE}/risk-financing/coverages/${coverageId}/`,
    {
      withCredentials: true,
      headers: {
        "X-CSRFToken": csrfToken,
      },
    }
  );

  return data;
}


export async function fetchCoverageAdequacy(
  simulations
) {
  const { data } = await axios.get(
    `${API_BASE}/risk-financing/coverage-adequacy/`,
    {
      params: {
        simulations,
      },
      withCredentials: true,
    }
  );

  return data;
}

export async function fetchPortfolioRiskAnalysis(simulations) {
  const { data } = await axios.get(
    `${API_BASE}/dashboard/risk-portfolio-analysis/`,
    {
      params: { investment: 0, simulations },
      withCredentials: true,
    }
  );
  return data;
}
